import { useEffect, useRef, useState } from "react";
import {
  advanceTutorial,
  buyHint,
  chapterFor,
  HINTS,
  nextLevel,
  puzzleCommand,
  quitPuzzle,
  replayLevel,
  setPaused,
  tutorialDef,
} from "../../app/actions.ts";
import { bridge } from "../../app/bridge.ts";
import { shallowEqual, useApp } from "../../app/store.ts";
import { arcadeVi } from "../../i18n/arcade.vi.ts";
import { colorLabVi } from "../../i18n/colorlab.vi.ts";
import { chapterName, tutorialText } from "../../i18n/content.ts";
import { towerVi } from "../../i18n/logictower.vi.ts";
import { jadeVi } from "../../i18n/tilecastle.vi.ts";
import { formatDuration, vi } from "../../i18n/vi.ts";
import { workshopVi } from "../../i18n/workshop.vi.ts";
import { play, playStar } from "../../services/audio/sfx.ts";
import { isUnlocked, pendingGold } from "../../services/save/progress.ts";
import { DoubleGoldOffer, HintAdOffer } from "../ads/AdsUi.tsx";
import { ClawHudLine, ClawStuck } from "../arcade/ClawHud.tsx";
import { LabHudLine, LabRecipes, LabStuck } from "../colorlab/LabHud.tsx";
import { Art, Button, Coins, IconButton, Sheet } from "../components/common.tsx";
import { centerOf, fx } from "../fx/fx.ts";
import { TowerHudLine } from "../logictower/TowerHud.tsx";
import { JadeHudLine, JadeStuck } from "../tilecastle/JadeHud.tsx";
import { SilkHudLine, SilkStuck } from "../workshop/SilkHud.tsx";

/** What the open tutorial step points at, for HUD highlights. */
function useTutorialFocus(): string | null {
  return useApp((s) => {
    const { tutorial, puzzle } = s.ui;
    if (!tutorial || !puzzle) return null;
    return tutorialDef(puzzle.houseId, tutorial.id)?.steps[tutorial.step]?.focus ?? null;
  });
}

export function PuzzleHud() {
  const { hud, puzzle, paidHints } = useApp((s) => ({ hud: s.ui.hud, puzzle: s.ui.puzzle, paidHints: s.ui.paidHints }), shallowEqual);
  const focus = useTutorialFocus();
  const [sheet, setSheet] = useState<"confirm" | "buy" | null>(null);
  if (!puzzle) return null;
  const level = puzzle.level;
  const used = hud?.hints ?? 0;
  const left = Math.max(0, HINTS.freePerLevel + paidHints - used);
  const onHint = () => {
    if (left === 0) {
      play("open");
      setSheet("buy");
    } else if (used === 0) setSheet("confirm");
    else puzzleCommand("hint");
  };
  return (
    <>
      <div className="puzzle-hud" data-testid="puzzle-hud">
        <div className="puzzle-hud__bar">
          <IconButton icon="icon.back" label={vi.puzzle.pauseTitle} onClick={() => setPaused(true)} testId="puzzle-pause" />
          <div className="puzzle-hud__title">
            <span className="puzzle-hud__level">
              <strong data-testid="puzzle-level">{vi.puzzle.level(level.id)}</strong>
              <span className={`diff-tag diff-tag--${level.difficulty}`}>
                {level.id % 10 === 0 ? vi.puzzle.finale : vi.puzzle.difficulty[level.difficulty]}
              </span>
            </span>
            {puzzle.houseId === "arcade" ? (
              <ClawHudLine coins={hud?.houseId === "arcade" ? hud.coins : 0} target={puzzle.level.threeStarCoins} focus={focus === "target"} />
            ) : puzzle.houseId === "fill" ? (
              <SilkHudLine rolls={hud?.houseId === "fill" ? hud.rolls : 0} target={puzzle.level.threeStarRolls} focus={focus === "target"} />
            ) : puzzle.houseId === "logic" ? (
              <TowerHudLine drops={hud?.houseId === "logic" ? hud.drops : 0} target={puzzle.level.threeStarDrops} focus={focus === "target"} />
            ) : puzzle.houseId === "tiles" ? (
              <JadeHudLine left={hud?.houseId === "tiles" ? hud.left : 0} goldLine={puzzle.level.goldLine} />
            ) : puzzle.houseId === "colors" ? (
              <LabHudLine pours={hud?.houseId === "colors" ? hud.pours : 0} target={puzzle.level.threeStarPours} focus={focus === "target"} />
            ) : hud?.houseId === "sorting" ? (
              <span className="puzzle-hud__moves">
                <Art id="icon.moves" className="ico-inline" />
                {vi.puzzle.moves(hud.actions, hud.maxMoves)}
                {hud.maxMoves === null ? (
                  <span className="puzzle-hud__target" aria-label={vi.puzzle.target(hud.targetMoves)}>
                    <Art id="icon.target" className="ico-inline" />
                    {hud.targetMoves}
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
          <div className="puzzle-hud__actions">
            <span className={focus === "undo" ? "hud-focus" : undefined}>
              <IconButton icon="icon.undo" label={vi.puzzle.undo} onClick={() => puzzleCommand("undo")} disabled={!hud?.canUndo} testId="puzzle-undo" />
            </span>
            <HintButton left={left} onClick={onHint} focus={focus === "hint"} />
            <IconButton icon="icon.restart" label={vi.puzzle.restart} onClick={() => puzzleCommand("restart")} testId="puzzle-restart" />
          </div>
        </div>
        {puzzle.houseId === "colors" ? (
          <>
            {puzzle.level.rules.mixes ? <LabRecipes mixes={puzzle.level.rules.mixes} focus={focus === "recipes"} /> : null}
            {hud?.houseId === "colors" && hud.stuck ? <LabStuck /> : null}
          </>
        ) : null}
        {hud?.houseId === "tiles" && hud.stuck ? <JadeStuck /> : null}
        {hud?.houseId === "fill" && hud.stuck ? <SilkStuck /> : null}
        {hud?.houseId === "arcade" && hud.stuck ? <ClawStuck /> : null}
      </div>
      {sheet === "confirm" ? (
        <Sheet title={vi.puzzle.hint} onClose={() => setSheet(null)} testId="hint-confirm">
          <p>
            {puzzle.houseId === "arcade"
              ? arcadeVi.hintCost
              : puzzle.houseId === "fill"
              ? workshopVi.hintCost
              : puzzle.houseId === "logic"
              ? towerVi.hintCost
              : puzzle.houseId === "tiles"
                ? jadeVi.hintCost
                : puzzle.houseId === "colors"
                  ? colorLabVi.hintCost
                  : vi.puzzle.hintCost}
          </p>
          <p className="note">{vi.puzzle.hintFree(left)}</p>
          <Button
            onClick={() => {
              setSheet(null);
              puzzleCommand("hint");
            }}
            testId="hint-yes"
          >
            {vi.puzzle.hintYes}
          </Button>
          <Button variant="ghost" onClick={() => setSheet(null)}>
            {vi.cancel}
          </Button>
        </Sheet>
      ) : null}
      {sheet === "buy" ? <BuyHintSheet onClose={() => setSheet(null)} /> : null}
    </>
  );
}

/** The bulb shows the free hints left, or a coin once they are used up. */
function HintButton({ left, onClick, focus }: { left: number; onClick: () => void; focus: boolean }) {
  const label = left > 0 ? vi.puzzle.hintLeft(left) : vi.puzzle.hintPrice(HINTS.priceGold);
  return (
    <button
      type="button"
      className={`icon-btn hint-btn ${focus ? "hud-focus" : ""}`}
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid="puzzle-hint"
    >
      <Art id="icon.hint" />
      {left > 0 ? (
        <span className="hint-btn__badge" data-testid="hint-left">
          {left}
        </span>
      ) : (
        <span className="hint-btn__badge hint-btn__badge--coin" data-testid="hint-coin">
          <Art id="reward.coin" />
        </span>
      )}
    </button>
  );
}

/** Out of free hints: trade server-verified gold for one more. */
function BuyHintSheet({ onClose }: { onClose: () => void }) {
  const { gold, pending, online } = useApp(
    (s) => ({ gold: s.save?.server?.wallet.gold ?? 0, pending: pendingGold(s.outbox), online: s.ui.online }),
    shallowEqual,
  );
  const [busy, setBusy] = useState(false);
  // Pending gold syncs right before the purchase, so it counts towards the price.
  const affordable = gold + pending >= HINTS.priceGold;
  return (
    <Sheet title={vi.puzzle.hintBuyTitle} {...(busy ? {} : { onClose })} testId="hint-buy">
      <Art id="icon.hint" className="hint-buy__art" />
      <HintAdOffer onDone={onClose} />
      <p>{vi.puzzle.hintBuyBody(HINTS.priceGold)}</p>
      <p className="hint-buy__wallet">
        <span>{vi.puzzle.hintWallet}</span>
        <Coins amount={gold} pending={pending} />
      </p>
      {!online ? <p className="note">{vi.puzzle.hintOffline}</p> : !affordable ? <p className="note">{vi.puzzle.hintPoor}</p> : null}
      <Button
        variant="gold"
        icon="reward.coin"
        disabled={busy || !online || !affordable}
        onClick={() => {
          setBusy(true);
          void buyHint().then((ok) => {
            setBusy(false);
            if (ok) onClose();
          });
        }}
        testId="hint-buy-yes"
      >
        {vi.puzzle.hintBuy(HINTS.priceGold)}
      </Button>
      <Button variant="ghost" onClick={onClose} disabled={busy}>
        {vi.puzzle.hintNo}
      </Button>
    </Sheet>
  );
}

export function PauseMenu() {
  return (
    <Sheet title={vi.puzzle.pauseTitle} onClose={() => setPaused(false)} testId="pause-menu">
      <Button onClick={() => setPaused(false)} testId="pause-resume">
        {vi.puzzle.resume}
      </Button>
      <Button
        variant="secondary"
        onClick={() => {
          setPaused(false);
          puzzleCommand("restart");
        }}
      >
        {vi.puzzle.restart}
      </Button>
      <Button
        variant="ghost"
        onClick={() => {
          setPaused(false);
          void quitPuzzle();
        }}
        testId="pause-quit"
      >
        {vi.puzzle.quit}
      </Button>
    </Sheet>
  );
}

/** Focus targets drawn by the Color Lab scene; the others are HUD highlights. */
const LAB_SCENE_FOCUS: ReadonlySet<string> = new Set(["vials", "spare", "demo", "mist", "sizes", "seal", "flask"]);
/**
 * Focus targets drawn by the Lối Ngọc scene. Satchel steps and the demo put the
 * bubble at the top: the satchel is at the bottom, and the first step always is
 * too (the entrance sits on the bottom row).
 */
const JADE_SCENE_FOCUS: ReadonlySet<string> = new Set(["floor", "demo", "bag", "gold", "stone", "double", "lock", "mirror", "hidden", "throne"]);
const JADE_TOP_FOCUS: ReadonlySet<string> = new Set(["bag", "gold", "demo"]);
/** Focus targets drawn by the Tháp Bi Lật scene; steps about the cups at the bottom put the bubble at the top. */
const TOWER_SCENE_FOCUS: ReadonlySet<string> = new Set(["tower", "hopper", "cups", "demo", "lever", "gutter", "gear", "lid", "keyed", "hoppers"]);
const TOWER_TOP_FOCUS: ReadonlySet<string> = new Set(["cups", "gutter", "lid"]);
/** Focus targets drawn by the Xưởng Phép Thuật scene; the shelf sits at the bottom, so its step puts the bubble at the top. */
const SILK_SCENE_FOCUS: ReadonlySet<string> = new Set(["shelf", "box", "bows", "scissors", "threads", "demo"]);
const SILK_TOP_FOCUS: ReadonlySet<string> = new Set(["shelf", "demo"]);
/** Focus targets drawn by the claw machine scene; its wish shelf sits at the bottom, so those steps put the bubble at the top. */
const CLAW_SCENE_FOCUS: ReadonlySet<string> = new Set(["machine", "wish", "grips", "chute", "knock", "demo", "tall", "heavy", "ribbon", "glass", "bouncy"]);
const CLAW_TOP_FOCUS: ReadonlySet<string> = new Set(["wish", "knock", "demo", "chute", "tall", "heavy", "ribbon", "bouncy"]);

/** Just-in-time tutorial bubble; the scene or the HUD highlights the focus target. */
export function TutorialBubble() {
  const { tutorial, houseId } = useApp((s) => ({ tutorial: s.ui.tutorial, houseId: s.ui.puzzle?.houseId ?? null }), shallowEqual);
  const [ready, setReady] = useState(false);
  const [board, setBoard] = useState(() => bridge.latest("lab:board") ?? null);
  useEffect(() => bridge.on("puzzle:ready", () => setReady(true)), []);
  useEffect(() => bridge.on("lab:board", setBoard), []);
  useEffect(() => {
    // The scene may already be up when the bubble mounts (replays, quick loads).
    const timer = setTimeout(() => setReady(true), 600);
    return () => clearTimeout(timer);
  }, []);
  const def = tutorial && houseId ? tutorialDef(houseId, tutorial.id) : undefined;
  const step = def && tutorial ? def.steps[tutorial.step] : undefined;
  const focus = step?.focus ?? null;
  useEffect(() => {
    if (!ready || !step) return;
    if (houseId === "arcade") bridge.emit("claw:tutorial-focus", focus && CLAW_SCENE_FOCUS.has(focus) ? (focus as never) : null);
    else if (houseId === "fill") bridge.emit("silk:tutorial-focus", focus && SILK_SCENE_FOCUS.has(focus) ? (focus as never) : null);
    else if (houseId === "logic") bridge.emit("tower:tutorial-focus", focus && TOWER_SCENE_FOCUS.has(focus) ? (focus as never) : null);
    else if (houseId === "tiles") bridge.emit("jade:tutorial-focus", focus && JADE_SCENE_FOCUS.has(focus) ? (focus as never) : null);
    else if (houseId === "colors") bridge.emit("lab:tutorial-focus", focus && LAB_SCENE_FOCUS.has(focus) ? (focus as never) : null);
    else bridge.emit("puzzle:tutorial-focus", (focus as never) ?? null);
  }, [ready, step, focus, houseId]);
  if (!tutorial || !step) return null;
  const last = def ? tutorial.step === def.steps.length - 1 : true;
  const demo = houseId === "colors" || houseId === "tiles" || houseId === "logic" || houseId === "fill" || houseId === "arcade" ? focus === "demo" : "demo" in step && Boolean(step.demo);
  // Steps about the board put the bubble where the vials leave the most room (usually below a single row).
  const roomBelow = board ? (typeof window === "undefined" ? 0 : window.innerHeight) - board.bottom : 0;
  const top =
    houseId === "arcade"
      ? focus !== null && CLAW_TOP_FOCUS.has(focus)
      : houseId === "fill"
      ? focus !== null && SILK_TOP_FOCUS.has(focus)
      : houseId === "logic"
      ? focus !== null && TOWER_TOP_FOCUS.has(focus)
      : houseId === "tiles"
      ? focus !== null && JADE_TOP_FOCUS.has(focus)
      : houseId === "colors" && focus !== null && LAB_SCENE_FOCUS.has(focus) && roomBelow < 250;
  const clearFocus =
    houseId === "arcade"
      ? "claw:tutorial-focus"
      : houseId === "fill"
      ? "silk:tutorial-focus"
      : houseId === "logic"
      ? "tower:tutorial-focus" : houseId === "tiles" ? "jade:tutorial-focus" : houseId === "colors" ? "lab:tutorial-focus" : "puzzle:tutorial-focus";
  return (
    <div className={`tutorial ${top ? "tutorial--top" : ""}`} data-testid="tutorial">
      <div className="tutorial__bubble">
        <Art id="reward.sparkle" className="tutorial__icon" />
        <p>{houseId ? tutorialText(houseId, tutorial.id, tutorial.step, step.text) : step.text}</p>
        <Button
          onClick={() => {
            // Keep a demo running until the first move; clear other highlights.
            if (last && !demo) bridge.emit(clearFocus, null);
            void advanceTutorial();
          }}
          testId="tutorial-ok"
        >
          {vi.puzzle.tutorialOk}
        </Button>
      </div>
    </div>
  );
}

export function OutOfMoves() {
  return (
    <Sheet title={vi.puzzle.outOfMoves} testId="out-of-moves">
      <Art id="reward.heart" className="result__art" />
      <Button onClick={() => puzzleCommand("restart")} testId="retry">
        {vi.puzzle.retry}
      </Button>
      <Button variant="ghost" onClick={() => void quitPuzzle()}>
        {vi.puzzle.quit}
      </Button>
    </Sheet>
  );
}

export function ResultScreen() {
  const { result, save } = useApp((s) => ({ result: s.ui.result, save: s.save }), shallowEqual);
  const [revealed, setRevealed] = useState(0);
  const [shownGold, setShownGold] = useState(0);
  const [skipped, setSkipped] = useState(false);
  const starRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const goldRef = useRef<HTMLParagraphElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const gold = result ? (result.status === "confirmed" ? (result.confirmedGold ?? 0) : result.predictedGold) : 0;
  const stars = result?.stars ?? 1;
  const chapterDone = result?.chapterComplete ?? null;
  const newRecord =
    result !== null && result.timeMs !== null && result.previousBestTimeMs !== null && result.timeMs < result.previousBestTimeMs;

  // Choreography: stars land one by one (rising notes), then coins fly into the gold line.
  useEffect(() => {
    if (!result) return;
    const after = (ms: number, run: () => void) => timers.current.push(setTimeout(run, ms));
    for (let i = 1; i <= stars; i += 1) {
      after(260 + i * 300, () => {
        setRevealed(i);
        playStar(i as 1 | 2 | 3);
        const at = centerOf(starRefs.current[i - 1] ?? null);
        if (at) fx.sparkles(at.x, at.y, i === 3 ? 18 : 10, i === 3 ? 170 : 120);
      });
    }
    const afterStars = 260 + stars * 300 + 280;
    after(afterStars, () => {
      const top = centerOf(starRefs.current[1] ?? null);
      if (stars === 3) {
        play("perfect");
        if (top) fx.fireworks(top.x, top.y - 30, 3);
      }
      if (chapterDone) {
        play("chapter");
        fx.confetti({ count: 160 });
      } else if (stars >= 2) fx.confetti({ count: stars === 3 ? 110 : 50 });
      if (newRecord) play("record");
      const target = centerOf(goldRef.current);
      if (gold > 0 && top && target) {
        const steps = Math.min(12, Math.max(4, Math.ceil(gold / 8)));
        let landed = 0;
        fx.coins(top, target, steps, () => {
          landed += 1;
          play("coin-tick");
          setShownGold(Math.round((gold * landed) / steps));
        });
      } else setShownGold(gold);
    });
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
    };
    // The card is keyed by attempt; replaying the choreography on gold updates would double it.
  }, [result?.levelId]);

  if (!result || !save) return null;
  const skip = () => {
    if (skipped) return;
    for (const timer of timers.current) clearTimeout(timer);
    setSkipped(true);
    setRevealed(stars);
  };
  const displayedGold = skipped || shownGold >= gold ? gold : shownGold;
  const improved = result.previousBest !== null && result.stars > result.previousBest;
  const replay = result.previousBest !== null;
  const canNext = result.levelId < 100 && isUnlocked(save, result.houseId, result.levelId + 1);
  return (
    <div className={`result ${skipped ? "result--skipped" : ""} result--stars-${stars}`} onPointerDown={skip} data-testid="result">
      <div className="result__card">
        {chapterDone ? (
          <div className="result__ribbon" data-testid="chapter-complete">
            <span>{vi.result.chapterDone(chapterDone)}</span>
            <small>{chapterName(result.houseId, chapterFor(result.houseId, result.levelId))}</small>
          </div>
        ) : null}
        <h2 className="result__title">{vi.result.titleByStars[stars] ?? vi.result.title}</h2>
        <div className="result__stars" aria-label={vi.starsLabel(stars)}>
          <span className="result__rays" aria-hidden="true" />
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              ref={(element) => {
                starRefs.current[n - 1] = element;
              }}
              className={`rstar rstar--${n} ${n <= revealed ? "rstar--on" : ""}`}
            >
              <Art id="reward.star-empty" className="rstar__empty" />
              {n <= stars ? <Art id="reward.star" className="rstar__full" /> : null}
            </span>
          ))}
        </div>
        {result.pours ? (
          <>
            <p className="lab-result__line" data-testid="result-pours">
              {colorLabVi.resultPours(result.pours.used, result.pours.optimal)}
            </p>
            {stars < 3 ? (
              <p className="note">{result.pours.hinted ? colorLabVi.resultHint : colorLabVi.resultRule(result.pours.three, result.pours.two)}</p>
            ) : null}
          </>
        ) : null}
        {result.drops ? (
          <>
            <p className="lab-result__line" data-testid="result-drops">
              {towerVi.resultDrops(result.drops.used, result.drops.optimal)}
            </p>
            {stars < 3 ? <p className="note">{result.drops.hinted ? towerVi.resultHint : towerVi.resultRule(result.drops.three, result.drops.two)}</p> : null}
          </>
        ) : null}
        {result.coins ? (
          <>
            <p className="lab-result__line" data-testid="result-coins">
              {arcadeVi.resultCoins(result.coins.used, result.coins.optimal)}
            </p>
            {stars < 3 ? <p className="note">{result.coins.hinted ? arcadeVi.resultHint : arcadeVi.resultRule(result.coins.three, result.coins.two)}</p> : null}
          </>
        ) : null}
        {result.rolls ? (
          <>
            <p className="lab-result__line" data-testid="result-rolls">
              {workshopVi.resultRolls(result.rolls.used, result.rolls.optimal)}
            </p>
            {stars < 3 ? <p className="note">{result.rolls.hinted ? workshopVi.resultHint : workshopVi.resultRule(result.rolls.three, result.rolls.two)}</p> : null}
          </>
        ) : null}
        {result.satchel ? (
          <>
            <p className="lab-result__line" data-testid="result-satchel">
              {jadeVi.resultSatchel(result.satchel.peak, result.satchel.tidiest)}
            </p>
            {stars < 3 ? <p className="note">{result.satchel.hinted ? jadeVi.resultHint : jadeVi.resultRule(result.satchel.goldLine)}</p> : null}
          </>
        ) : null}
        {result.timeMs !== null ? (
          <p className="result__time" data-testid="result-time">
            <Art id="icon.timer" className="ico-inline" />
            {vi.result.time(formatDuration(result.timeMs))}
            {newRecord ? (
              <strong className="result__record">
                <Art id="icon.record" className="ico-inline" />
                {vi.result.newRecord}
              </strong>
            ) : null}
          </p>
        ) : null}
        {result.bestCombo >= 5 ? (
          <p className="result__combo">
            <Art id="icon.combo" className="ico-inline" />
            {vi.result.combo(result.bestCombo)}
          </p>
        ) : null}
        {result.status === "save_failed" ? (
          <p className="result__warn">{vi.result.saveFailed}</p>
        ) : (
          <>
            {gold > 0 ? (
              <p className={`result__gold result__gold--${result.status}`} data-testid="result-gold" ref={goldRef}>
                <Art id="reward.coin" className="result__coin" /> {vi.result.gold(displayedGold)}
              </p>
            ) : null}
            <p className="result__status" data-testid="result-status">
              {result.status === "offline"
                ? vi.result.pendingOffline
                : result.status === "confirmed"
                  ? vi.result.confirmed
                  : result.status === "rejected"
                    ? vi.result.needsUpdate
                    : vi.result.pending}
            </p>
            {gold > 0 && result.previousBest === null ? <DoubleGoldOffer houseId={result.houseId} levelId={result.levelId} /> : null}
            {improved && result.previousBest ? <p>{vi.result.improved(result.previousBest, result.stars)}</p> : null}
            {replay && !improved ? <p className="note">{vi.result.replay}</p> : null}
          </>
        )}
        <div className="result__actions">
          {canNext ? (
            <Button onClick={() => nextLevel()} testId="result-next">
              {vi.result.next}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => replayLevel()} testId="result-again">
            {vi.result.again}
          </Button>
          <Button variant="ghost" onClick={() => void quitPuzzle()} testId="result-city">
            {vi.result.city}
          </Button>
        </div>
      </div>
    </div>
  );
}
